import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { 
  Menu, Send, Mail, MessageSquare, CheckCircle, XCircle, Clock,
  Settings, AlertCircle, Zap, Calendar, Bell
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AdminSidebar from "@/components/AdminSidebar";
import { useNotificationCount } from "@/hooks/useNotificationCount";

const API = "/api";

const Notifications = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const notificationCount = useNotificationCount();
  const [jobs, setJobs] = useState([]);
  const [pendingReminders, setPendingReminders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settings, setSettings] = useState({
    auto_reminder_enabled: true,
    reminder_hours_before: 24,
    default_send_email: true,
    default_send_sms: true,
    reminder_message_template: "Reminder: Your lawn service is scheduled for {date} at {time}. Address: {address}."
  });
  const [selectedJobs, setSelectedJobs] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [jobsRes, notifRes, settingsRes, pendingRes] = await Promise.all([
        axios.get(`${API}/jobs`, { withCredentials: true }),
        axios.get(`${API}/notifications`, { withCredentials: true }),
        axios.get(`${API}/notifications/settings`, { withCredentials: true }).catch(() => ({ data: settings })),
        axios.get(`${API}/notifications/pending-reminders`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      
      // Filter jobs that are scheduled (not completed or cancelled)
      setJobs(jobsRes.data.filter(j => j.status === "scheduled" || j.status === "in_progress"));
      setNotifications(notifRes.data);
      setSettings(settingsRes.data);
      setPendingReminders(pendingRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const sendReminder = async (jobId, sendEmail = true, sendSms = true) => {
    setSendingReminder(jobId);
    try {
      const response = await axios.post(`${API}/reminders/send`, {
        job_id: jobId,
        send_email: sendEmail,
        send_sms: sendSms,
      }, { withCredentials: true });
      
      const results = response.data;
      if (results.email?.success || results.sms?.success) {
        toast.success("Reminder sent successfully");
      } else {
        toast.warning("Reminder queued (notification services not configured)");
      }
      fetchData();
    } catch (error) {
      console.error("Failed to send reminder:", error);
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    } finally {
      setSendingReminder(null);
    }
  };

  const sendBulkReminders = async () => {
    if (selectedJobs.length === 0) {
      toast.error("Please select jobs to send reminders");
      return;
    }
    
    setSendingBulk(true);
    try {
      const response = await axios.post(`${API}/reminders/send-bulk`, {
        job_ids: selectedJobs,
        send_email: settings.default_send_email,
        send_sms: settings.default_send_sms,
      }, { withCredentials: true });
      
      const results = response.data;
      if (results.sent > 0) {
        toast.success(`Sent reminders for ${results.sent} job(s)`);
      }
      if (results.failed > 0) {
        toast.warning(`Failed to send ${results.failed} reminder(s)`);
      }
      setSelectedJobs([]);
      fetchData();
    } catch (error) {
      console.error("Failed to send bulk reminders:", error);
      toast.error("Failed to send bulk reminders");
    } finally {
      setSendingBulk(false);
    }
  };

  const sendAllPendingReminders = async () => {
    if (pendingReminders.length === 0) {
      toast.info("No pending reminders to send");
      return;
    }
    
    setSendingBulk(true);
    try {
      const response = await axios.post(`${API}/reminders/send-bulk`, {
        job_ids: pendingReminders.map(j => j.job_id),
        send_email: settings.default_send_email,
        send_sms: settings.default_send_sms,
      }, { withCredentials: true });
      
      const results = response.data;
      toast.success(`Processed ${results.sent} reminder(s)`);
      fetchData();
    } catch (error) {
      console.error("Failed to send reminders:", error);
      toast.error("Failed to send reminders");
    } finally {
      setSendingBulk(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/notifications/settings`, settings, { withCredentials: true });
      toast.success("Settings saved successfully");
      setSettingsOpen(false);
      fetchData();
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleJobSelection = (jobId) => {
    setSelectedJobs(prev => 
      prev.includes(jobId) 
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  const selectAllJobs = () => {
    const unsentJobs = jobs.filter(j => !j.reminder_sent);
    if (selectedJobs.length === unsentJobs.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(unsentJobs.map(j => j.job_id));
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "MMM d, yyyy 'at' h:mm a");
    } catch {
      return dateStr;
    }
  };

  const formatShortDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "MMM d");
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} notificationCount={notificationCount} />

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#0A0A0A]/80 backdrop-blur-lg border-b border-[#27272A] p-4 lg:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                className="lg:hidden text-[#A1A1AA] hover:text-white"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-heading text-2xl lg:text-3xl font-bold text-white uppercase">Notifications</h1>
                <p className="text-[#A1A1AA] text-sm">Send reminders to customers and staff</p>
              </div>
            </div>
            <Button
              data-testid="notification-settings-btn"
              variant="outline"
              className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-white"
              onClick={() => setSettingsOpen(true)}
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
          </div>
        </header>

        <div className="p-4 lg:p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-[#39FF14] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <Tabs defaultValue="pending" className="space-y-6">
              <TabsList className="bg-[#121212] border border-[#27272A]">
                <TabsTrigger 
                  value="pending" 
                  className="data-[state=active]:bg-[#39FF14]/10 data-[state=active]:text-[#39FF14]"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Due Soon ({pendingReminders.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="all" 
                  className="data-[state=active]:bg-[#39FF14]/10 data-[state=active]:text-[#39FF14]"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  All Upcoming ({jobs.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="history" 
                  className="data-[state=active]:bg-[#39FF14]/10 data-[state=active]:text-[#39FF14]"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  History ({notifications.length})
                </TabsTrigger>
              </TabsList>

              {/* Due Soon Tab - Jobs needing reminders based on settings */}
              <TabsContent value="pending">
                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="font-heading text-xl font-bold text-white uppercase">Reminders Due</h2>
                      <p className="text-[#A1A1AA] text-sm">
                        Jobs scheduled within the next {settings.reminder_hours_before} hours
                      </p>
                    </div>
                    {pendingReminders.length > 0 && (
                      <Button
                        data-testid="send-all-pending-btn"
                        onClick={sendAllPendingReminders}
                        disabled={sendingBulk}
                        className="bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                      >
                        {sendingBulk ? (
                          <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Send All ({pendingReminders.length})
                      </Button>
                    )}
                  </div>

                  {pendingReminders.length === 0 ? (
                    <div className="text-center py-12">
                      <CheckCircle className="w-16 h-16 text-[#39FF14] mx-auto mb-4" />
                      <p className="text-white text-lg font-medium">All caught up!</p>
                      <p className="text-[#A1A1AA]">No reminders due at this time</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingReminders.map((job) => (
                        <div 
                          key={job.job_id}
                          data-testid={`pending-reminder-${job.job_id}`}
                          className="p-4 bg-[#0A0A0A] border-2 border-yellow-400/30 rounded-lg"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="text-white font-semibold">{job.customer_name}</h3>
                              <p className="text-[#A1A1AA] text-sm">{job.customer_address}</p>
                            </div>
                            <span className="flex items-center gap-1 text-yellow-400 text-xs px-2 py-1 bg-yellow-400/10 rounded-full">
                              <AlertCircle className="w-4 h-4" />
                              Due Soon
                            </span>
                          </div>
                          <div className="text-[#A1A1AA] text-sm mb-4">
                            <p>Scheduled: {formatDate(job.scheduled_date)} at {job.scheduled_time}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <Button
                              size="sm"
                              className="bg-[#064E3B] text-[#39FF14] hover:bg-[#065F46]"
                              onClick={() => sendReminder(job.job_id, true, false)}
                              disabled={sendingReminder === job.job_id}
                            >
                              <Mail className="w-4 h-4 mr-2" />
                              Email
                            </Button>
                            <Button
                              size="sm"
                              className="bg-[#064E3B] text-[#39FF14] hover:bg-[#065F46]"
                              onClick={() => sendReminder(job.job_id, false, true)}
                              disabled={sendingReminder === job.job_id}
                            >
                              <MessageSquare className="w-4 h-4 mr-2" />
                              SMS
                            </Button>
                            <Button
                              size="sm"
                              className="bg-[#39FF14] text-black hover:bg-[#32D711]"
                              onClick={() => sendReminder(job.job_id, true, true)}
                              disabled={sendingReminder === job.job_id}
                            >
                              {sendingReminder === job.job_id ? (
                                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                              ) : (
                                <Send className="w-4 h-4 mr-2" />
                              )}
                              Both
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* All Upcoming Jobs Tab */}
              <TabsContent value="all">
                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="font-heading text-xl font-bold text-white uppercase">All Upcoming Jobs</h2>
                      <p className="text-[#A1A1AA] text-sm">
                        Select jobs to send reminders in bulk
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {jobs.filter(j => !j.reminder_sent).length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={selectAllJobs}
                          className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]"
                        >
                          {selectedJobs.length === jobs.filter(j => !j.reminder_sent).length ? "Deselect All" : "Select All"}
                        </Button>
                      )}
                      {selectedJobs.length > 0 && (
                        <Button
                          data-testid="send-selected-btn"
                          onClick={sendBulkReminders}
                          disabled={sendingBulk}
                          className="bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                        >
                          {sendingBulk ? (
                            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                          ) : (
                            <Send className="w-4 h-4 mr-2" />
                          )}
                          Send Selected ({selectedJobs.length})
                        </Button>
                      )}
                    </div>
                  </div>

                  {jobs.length === 0 ? (
                    <div className="text-center py-12">
                      <Calendar className="w-16 h-16 text-[#27272A] mx-auto mb-4" />
                      <p className="text-[#A1A1AA]">No upcoming jobs to send reminders for</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {jobs.map((job) => (
                        <div 
                          key={job.job_id}
                          data-testid={`reminder-job-${job.job_id}`}
                          className={`p-4 bg-[#0A0A0A] border rounded-lg transition-all ${
                            selectedJobs.includes(job.job_id) 
                              ? "border-[#39FF14]" 
                              : "border-[#27272A]"
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            {!job.reminder_sent && (
                              <input
                                type="checkbox"
                                checked={selectedJobs.includes(job.job_id)}
                                onChange={() => toggleJobSelection(job.job_id)}
                                className="mt-1 w-5 h-5 rounded border-[#27272A] bg-[#0A0A0A] text-[#39FF14] focus:ring-[#39FF14]"
                              />
                            )}
                            <div className="flex-1">
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <h3 className="text-white font-semibold">{job.customer_name}</h3>
                                  <p className="text-[#A1A1AA] text-sm">{job.customer_address}</p>
                                </div>
                                {job.reminder_sent ? (
                                  <span className="flex items-center gap-1 text-[#39FF14] text-xs">
                                    <CheckCircle className="w-4 h-4" />
                                    Sent
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1 text-yellow-400 text-xs">
                                    <Clock className="w-4 h-4" />
                                    Pending
                                  </span>
                                )}
                              </div>
                              <div className="text-[#A1A1AA] text-sm mb-4">
                                <p>Scheduled: {formatDate(job.scheduled_date)} at {job.scheduled_time}</p>
                              </div>
                              {!job.reminder_sent && (
                                <div className="flex items-center gap-4">
                                  <Button
                                    data-testid={`send-email-${job.job_id}`}
                                    size="sm"
                                    className="bg-[#064E3B] text-[#39FF14] hover:bg-[#065F46]"
                                    onClick={() => sendReminder(job.job_id, true, false)}
                                    disabled={sendingReminder === job.job_id}
                                  >
                                    <Mail className="w-4 h-4 mr-2" />
                                    Email
                                  </Button>
                                  <Button
                                    data-testid={`send-sms-${job.job_id}`}
                                    size="sm"
                                    className="bg-[#064E3B] text-[#39FF14] hover:bg-[#065F46]"
                                    onClick={() => sendReminder(job.job_id, false, true)}
                                    disabled={sendingReminder === job.job_id}
                                  >
                                    <MessageSquare className="w-4 h-4 mr-2" />
                                    SMS
                                  </Button>
                                  <Button
                                    data-testid={`send-both-${job.job_id}`}
                                    size="sm"
                                    className="bg-[#39FF14] text-black hover:bg-[#32D711]"
                                    onClick={() => sendReminder(job.job_id, true, true)}
                                    disabled={sendingReminder === job.job_id}
                                  >
                                    {sendingReminder === job.job_id ? (
                                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                                    ) : (
                                      <Send className="w-4 h-4 mr-2" />
                                    )}
                                    Both
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* History Tab */}
              <TabsContent value="history">
                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
                  <h2 className="font-heading text-xl font-bold text-white uppercase mb-6">Notification History</h2>
                  
                  {notifications.length === 0 ? (
                    <div className="text-center py-12">
                      <Bell className="w-16 h-16 text-[#27272A] mx-auto mb-4" />
                      <p className="text-[#A1A1AA]">No notifications sent yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {notifications.map((notif) => (
                        <div 
                          key={notif.notification_id}
                          className="p-3 bg-[#0A0A0A] border border-[#27272A] rounded-lg"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {notif.type === "email" ? (
                                <Mail className="w-4 h-4 text-[#39FF14]" />
                              ) : (
                                <MessageSquare className="w-4 h-4 text-[#39FF14]" />
                              )}
                              <span className="text-white text-sm font-medium uppercase">{notif.type}</span>
                            </div>
                            {notif.status === "sent" ? (
                              <CheckCircle className="w-4 h-4 text-[#39FF14]" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-400" />
                            )}
                          </div>
                          <p className="text-[#A1A1AA] text-xs line-clamp-2">{notif.message}</p>
                          <p className="text-[#A1A1AA]/60 text-xs mt-2">{formatDate(notif.sent_at)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}

          {/* Configuration Notice */}
          <div className="mt-6 p-4 bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-xl">
            <div className="flex items-start gap-3">
              <Bell className="w-5 h-5 text-[#39FF14] mt-0.5" />
              <div>
                <h3 className="text-[#39FF14] font-semibold mb-1">Email Notifications Active</h3>
                <p className="text-[#A1A1AA] text-sm">
                  Reminders are sent to customers via Gmail (zigzaglawn@gmail.com) and a copy is sent to your inbox.
                  Configure SMS via Vonage in Subscriptions &gt; Settings.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#39FF14]" />
              Notification Settings
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Auto Reminder */}
            <div className="flex items-center justify-between p-4 bg-[#0A0A0A] rounded-lg">
              <div>
                <Label className="text-white font-medium">Auto Reminders</Label>
                <p className="text-[#A1A1AA] text-xs">Automatically flag jobs needing reminders</p>
              </div>
              <Switch
                checked={settings.auto_reminder_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, auto_reminder_enabled: checked })}
                className="data-[state=checked]:bg-[#39FF14]"
              />
            </div>

            {/* Reminder Timing */}
            <div className="p-4 bg-[#0A0A0A] rounded-lg space-y-3">
              <Label className="text-white font-medium">Reminder Timing</Label>
              <p className="text-[#A1A1AA] text-xs">Send reminders this many hours before a job</p>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min="1"
                  max="168"
                  value={settings.reminder_hours_before}
                  onChange={(e) => setSettings({ ...settings, reminder_hours_before: parseInt(e.target.value) || 24 })}
                  className="bg-[#121212] border-[#27272A] text-white w-24"
                />
                <span className="text-[#A1A1AA]">hours before</span>
              </div>
            </div>

            {/* Default Methods */}
            <div className="p-4 bg-[#0A0A0A] rounded-lg space-y-4">
              <Label className="text-white font-medium">Default Notification Methods</Label>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#A1A1AA]" />
                  <span className="text-[#EDEDED]">Send via Email</span>
                </div>
                <Switch
                  checked={settings.default_send_email}
                  onCheckedChange={(checked) => setSettings({ ...settings, default_send_email: checked })}
                  className="data-[state=checked]:bg-[#39FF14]"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-[#A1A1AA]" />
                  <span className="text-[#EDEDED]">Send via SMS</span>
                </div>
                <Switch
                  checked={settings.default_send_sms}
                  onCheckedChange={(checked) => setSettings({ ...settings, default_send_sms: checked })}
                  className="data-[state=checked]:bg-[#39FF14]"
                />
              </div>
            </div>

            {/* Save Button */}
            <Button
              data-testid="save-settings-btn"
              onClick={saveSettings}
              disabled={savingSettings}
              className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
            >
              {savingSettings ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                "Save Settings"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Notifications;
